import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import "./ListingCard.scss";
import SaveSheetModal from "../../Saved/SaveSheetModal";
import { getCurrentUserId } from "../../../awsAuth";
import { thumbImgProps } from "../../../utils/imageUrl";

const DEFAULT_IMAGE_PLACEHOLDER = "";

const CURRENCY_SYMBOL_MAP = {
  USD: "$",
  EUR: "€",
  PLN: "zł",
  GBP: "£",

  UAH: "ГРН",
  "₴": "ГРН",
  грн: "ГРН",
  ГРН: "ГРН",

  $: "$",
  "€": "€",
  "zł": "zł",
  "£": "£",
};

const formatCurrencyLabel = (c) => {
  const raw = c ?? "ГРН";
  const key = String(raw).trim();

  return CURRENCY_SYMBOL_MAP[key] || key;
};

const cleanTextValue = (value) => {
  if (value === null || value === undefined) return "";

  if (typeof value === "string" || typeof value === "number") {
    return String(value).replace(/\s+/g, " ").trim();
  }

  if (typeof value === "object") {
    const candidates = [
      value.city,
      value.location,
      value.name,
      value.label,
      value.title,
      value.value,
    ];

    for (const candidate of candidates) {
      const cleaned = cleanTextValue(candidate);
      if (cleaned) return cleaned;
    }
  }

  return "";
};

function SaveIcon({ filled = false }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="listing-card__save-svg"
    >
      <path
        d="M6.8 4.6C6.8 3.7 7.5 3 8.4 3h7.2c.9 0 1.6.7 1.6 1.6v15.2c0 .8-.9 1.2-1.5.7L12 17.3l-3.7 3.2c-.6.5-1.5.1-1.5-.7V4.6Z"
        fill={filled ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const posterThemes = [
  "blue",
  "violet",
  "graphite",
  "green",
  "bronze",
  "wine",
  "ocean",
  "sand",
];

const getStableTheme = (item, title) => {
  const source = String(
    item?.listingId || item?.id || item?._id || title || "listing"
  );

  let hash = 0;

  for (let i = 0; i < source.length; i += 1) {
    hash = source.charCodeAt(i) + ((hash << 5) - hash);
  }

  return posterThemes[Math.abs(hash) % posterThemes.length];
};

export default function ListingCard({
  item,
  isSaved,
  onOpenListingDetails,
  navigate,
  onSyncCounts,
}) {
  const navigateHook = useNavigate();

  const go = (to, options) => {
    if (typeof navigate === "function") {
      try {
        navigate(to, options);
        return;
      } catch {}
    }

    try {
      navigateHook(to, options);
    } catch {}
  };

  const listingId = useMemo(
    () => item?.id || item?.listingId || item?._id || null,
    [item]
  );

  const [saveSheetOpen, setSaveSheetOpen] = useState(false);
  const [localSaved, setLocalSaved] = useState(!!isSaved);

  useEffect(() => {
    setLocalSaved(!!isSaved);
  }, [isSaved, listingId]);

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};

      if (!detail.listingId || String(detail.listingId) !== String(listingId)) {
        return;
      }

      if (typeof detail.saved === "boolean") {
        setLocalSaved(!!detail.saved);
      }
    };

    window.addEventListener("cv-saved-updated", handler);

    return () => window.removeEventListener("cv-saved-updated", handler);
  }, [listingId]);

  const imageUrl = useMemo(() => {
    if (Array.isArray(item?.photos) && item.photos.length > 0) {
      return item.photos[0];
    }

    return item?.mainPhoto || item?.image || DEFAULT_IMAGE_PLACEHOLDER;
  }, [item]);

  const isWanted = useMemo(() => {
    const rawType =
      item?.listingType ??
      item?.type ??
      item?.transferType ??
      item?.dealType ??
      item?.raw?.type ??
      item?.raw?.transferType ??
      item?.raw?.dealType ??
      "";

    return String(rawType).trim().toLowerCase() === "wanted";
  }, [item]);

  const priceLabel = useMemo(() => {
    const rawType =
      item?.listingType ??
      item?.typeRaw ??
      item?.type ??
      item?.transferType ??
      item?.dealType ??
      item?.raw?.listingType ??
      item?.raw?.typeRaw ??
      item?.raw?.type ??
      item?.raw?.transferType ??
      item?.raw?.dealType ??
      "";

    const type = String(rawType).trim().toLowerCase();

    const rawExchangeFor =
      item?.exchangeFor ??
      item?.raw?.exchangeFor ??
      item?.attributes?.exchangeFor ??
      item?.raw?.attributes?.exchangeFor ??
      "";

    const exchangeFor = String(rawExchangeFor || "").trim();

    const currency = item.currency || item?.raw?.currency || "ГРН";
    const currencyLabel = formatCurrencyLabel(currency);
    const rawPrice = item.price ?? item?.raw?.price;

    if (type === "wanted") return "Шукаю";

    if (type === "exchange" || !!exchangeFor) {
      return "Обмін";
    }

    if (type === "free") return "Безкоштовно";

    const num = Number(rawPrice);

    if (Number.isFinite(num) && rawPrice !== null && rawPrice !== "") {
      if (num === 0) return "Безкоштовно";

      const pretty = num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);

      return `${pretty} ${currencyLabel}`;
    }

    return rawPrice ? `${rawPrice} ${currencyLabel}` : "—";
  }, [
    item?.type,
    item?.transferType,
    item?.dealType,
    item?.price,
    item?.currency,
    item?.exchangeFor,
    item?.raw,
    item?.attributes,
  ]);

  const title = String(item?.title || item?.name || "Оголошення").trim();

  const posterTheme = useMemo(() => {
    return getStableTheme(item, title);
  }, [item, title]);

  const locationLabel = useMemo(() => {
    return (
      cleanTextValue(item?.location) ||
      cleanTextValue(item?.city) ||
      cleanTextValue(item?.settlement) ||
      cleanTextValue(item?.place) ||
      cleanTextValue(item?.raw?.location) ||
      cleanTextValue(item?.raw?.city) ||
      cleanTextValue(item?.raw?.settlement) ||
      cleanTextValue(item?.raw?.place) ||
      cleanTextValue(item?.attributes?.location) ||
      cleanTextValue(item?.attributes?.city) ||
      cleanTextValue(item?.attributes?.settlement) ||
      cleanTextValue(item?.attributes?.place) ||
      ""
    );
  }, [item]);

  const shouldRenderCard = !!listingId && (title || imageUrl);

  const openListing = () => {
    if (typeof onOpenListingDetails === "function") {
      onOpenListingDetails();
      return;
    }

    if (listingId) {
      go(`/listing/${listingId}`);
    }
  };

  const openSaveSheet = (e) => {
    e?.stopPropagation?.();

    if (!listingId) return;

    const me = getCurrentUserId?.();

    if (!me) {
      go("/auth", { state: { from: `/listing/${listingId}` } });
      return;
    }

    setSaveSheetOpen(true);
  };

  if (!shouldRenderCard) return null;

  return (
    <article
      className={`listing-card listing-card--grid ${
        isWanted ? "is-wanted" : ""
      }`}
      onClick={openListing}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openListing();
        }
      }}
    >
      <div className="listing-card__image-wrap">
        {imageUrl ? (
          <img
            {...thumbImgProps(imageUrl)}
            alt={title}
            className="listing-card__image"
            loading="lazy"
          />
        ) : (
          <div
            className={`listing-card__poster listing-card__poster--${posterTheme}`}
          >
            <div className="listing-card__poster-glow" />

                        <div
              className={`listing-card__poster-title ${
                title.length <= 18
                  ? "is-short"
                  : title.length <= 42
                  ? "is-medium"
                  : "is-long"
              }`}
            >
              {title}
            </div>
          </div>
        )}

        <button
          type="button"
          className={`listing-card__save ${localSaved ? "is-saved" : ""}`}
          onClick={openSaveSheet}
          aria-label={localSaved ? "Збережено" : "Зберегти"}
          title={localSaved ? "Збережено" : "Зберегти"}
        >
          <SaveIcon filled={localSaved} />
        </button>
      </div>

      <div className="listing-card__body">
        <h3 className="listing-card__title">{title}</h3>

        <div className="listing-card__meta-row">
          <div className={`listing-card__price ${isWanted ? "is-wanted" : ""}`}>
            {priceLabel}
          </div>

          {locationLabel && (
            <div className="listing-card__city">{locationLabel}</div>
          )}
        </div>
      </div>

      {createPortal(
        <SaveSheetModal
          open={saveSheetOpen}
          onClose={() => setSaveSheetOpen(false)}
          userId={getCurrentUserId?.()}
          postId={listingId}
          type="listing"
          thumbnail={imageUrl}
          title="Збереження"
          onChanged={({ saved }) => {
            const willBeSaved = !!saved;

            setLocalSaved(willBeSaved);

            try {
              window.dispatchEvent(
                new CustomEvent("cv-saved-updated", {
                  detail: {
                    listingId,
                    saved: willBeSaved,
                  },
                })
              );
            } catch {}

            onSyncCounts?.({ listingId });
          }}
        />,
        document.getElementById("modal-root") || document.body
      )}
    </article>
  );
}